import { useState } from 'react';

import { Alert, Badge, Card, Checkbox, DataRow, IssueCard, PageGlyph } from '@/components/ui';
import { useAnnounceOnChange } from '@/lib/a11y';
import type { ValidationReport } from '@/lib/api/types';
import { cn } from '@/lib/cn';
import { formatPages } from '@/lib/format';

import {
  formatDetectedSize,
  formatPageScope,
  issueSeverity,
  issueTitle,
  outstandingAcknowledgements,
  verdictAnnouncement,
  verdictSeverity,
} from './issues';

export interface ValidationReportViewProps {
  report: ValidationReport;
  /** Codes the customer has ticked. Owned by the step, so it survives re-renders. */
  acknowledged: ReadonlySet<string>;
  onToggleAcknowledge: (code: string, checked: boolean) => void;
}

const VERDICT_HEADLINE: Record<ValidationReport['verdict'], string> = {
  ok: 'Your file is ready to print',
  warn: 'Your file is printable, with a few things to check',
  reject: 'We cannot print this file',
};

const VERDICT_BADGE: Record<
  ValidationReport['verdict'],
  { tone: 'success' | 'warning' | 'danger'; text: string }
> = {
  ok: { tone: 'success', text: 'Ready' },
  warn: { tone: 'warning', text: 'Check first' },
  reject: { tone: 'danger', text: 'Cannot print' },
};

/**
 * The validation report, rendered.
 *
 * Three rules this screen holds to:
 *
 *   1. THE SERVER'S WORDS, VERBATIM. Every issue message is prose the backend
 *      wrote for a customer, and it already ends with what to do about it.
 *      Rewriting it here would produce two versions of the truth, and the worse
 *      one would be the one shipped.
 *   2. SEVERITY IS NEVER COLOUR ALONE. Green/amber/red comes with a glyph, a
 *      badge word and a heading (see Alert/IssueCard).
 *   3. THE ACKNOWLEDGEMENT IS PER ISSUE, and it lives directly under the
 *      sentence it acknowledges. A single "I agree to everything" checkbox at
 *      the bottom of the page is a dark pattern with a checkbox on it.
 */
export function ValidationReportView({
  report,
  acknowledged,
  onToggleAcknowledge,
}: ValidationReportViewProps) {
  const issues = report.issues ?? [];
  const firstThumbnail = report.thumbnails?.[0];
  const [thumbnailBroken, setThumbnailBroken] = useState(false);
  const badge = VERDICT_BADGE[report.verdict];
  const outstanding = outstandingAcknowledgements(report, acknowledged);

  // Announced once per report, politely for ok/warn and assertively for a
  // reject — a blocked order is worth interrupting for.
  useAnnounceOnChange(
    verdictAnnouncement(report),
    report.verdict === 'reject' ? 'assertive' : 'polite',
  );

  return (
    // On desktop the verdict spans the width and the document sits beside what
    // we found in it — reading "here is the file" and "here is what is wrong
    // with it" together is the point of this screen.
    //
    // Two columns ONLY when there is a second thing to show. A clean file has no
    // issues card, and splitting the width anyway left the document card at half
    // size with its labels wrapping mid-phrase.
    <div
      className={cn(
        'flex flex-col gap-4',
        issues.length > 0 && 'lg:grid lg:grid-cols-2 lg:items-start lg:gap-6',
      )}
    >
      <Alert
        severity={verdictSeverity(report.verdict)}
        title={VERDICT_HEADLINE[report.verdict]}
        className={issues.length > 0 ? 'lg:col-span-2' : undefined}
      >
        {report.verdict === 'ok' ? (
          <p>We checked every page and found nothing that would spoil the print.</p>
        ) : report.verdict === 'warn' ? (
          <p>
            You can go ahead once you have read and confirmed the{' '}
            {issues.length === 1 ? 'point' : 'points'} below.
          </p>
        ) : (
          <p>Nothing you can confirm will make this printable. Please upload a corrected file.</p>
        )}
      </Alert>

      {/* A converted file needs one thing said out loud: the preview below is
          the CONVERSION, not the document they have on their machine. Fonts we
          do not have get substituted and the text reflows, and the only way
          that is caught before it is printed is somebody looking. */}
      {report.converted_from ? (
        <Alert
          severity="info"
          title={`Converted from your ${report.converted_from}`}
          className={issues.length > 0 ? 'lg:col-span-2' : undefined}
        >
          <p>
            We turned it into a PDF, and that is exactly what we will print. Check the preview — if
            a font was missing at our end the layout can shift.
          </p>
        </Alert>
      ) : null}

      <Card
        title="Your document"
        aside={
          <Badge tone={badge.tone} srPrefix="File check:">
            {badge.text}
          </Badge>
        }
      >
        <div className="flex gap-4">
          {firstThumbnail?.url && !thumbnailBroken ? (
            <img
              src={firstThumbnail.url}
              // Not "thumbnail" or "preview": a screen-reader user needs to know
              // what the image IS, and it is the first page of their own file.
              alt={`First page of the document you uploaded, ${formatPages(report.page_count)} in total`}
              width={96}
              height={136}
              onError={() => setThumbnailBroken(true)}
              className="h-[136px] w-24 shrink-0 rounded-control border border-line bg-surface-sunken object-contain"
            />
          ) : (
            <div className="grid h-[136px] w-24 shrink-0 place-items-center rounded-control border border-dashed border-line bg-surface-sunken text-ink-subtle">
              <PageGlyph />
              <span className="sr-only">No preview available for this file.</span>
            </div>
          )}

          <dl className="min-w-0 flex-1">
            <DataRow label="Pages" value={formatPages(report.page_count)} />
            <DataRow label="Paper size" value={formatDetectedSize(report)} />
            <DataRow
              label="Page sizes"
              value={report.uniform_size ? 'All the same' : 'Not all the same'}
            />
          </dl>
        </div>
      </Card>

      {issues.length > 0 ? (
        <Card
          title={issues.length === 1 ? 'What we found' : `What we found (${issues.length})`}
          flush
        >
          {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops
              list semantics from list-style:none lists; see Stepper for the same note. */}
          <ul className="list-none" role="list">
            {issues.map((issue) => (
              <IssueCard
                key={issue.code}
                severity={issueSeverity(issue.severity)}
                title={issueTitle(issue)}
                scope={formatPageScope(issue.pages)}
                detail={issue.message}
                action={
                  issue.acknowledgeable ? (
                    <Checkbox
                      checked={acknowledged.has(issue.code)}
                      onChange={(event) => onToggleAcknowledge(issue.code, event.target.checked)}
                      label="I understand — print it anyway"
                    />
                  ) : null
                }
              />
            ))}
          </ul>
        </Card>
      ) : null}

      {report.requires_acknowledgement && outstanding > 0 ? (
        <p className="text-sm text-ink-muted" data-testid="acknowledgement-hint">
          Tick {outstanding === 1 ? 'the box' : `all ${outstanding} boxes`} above to continue.
        </p>
      ) : null}
    </div>
  );
}
